import type { ImageGenerateParams } from '../base';
import { mapResolutionToSize } from '../../shared/image-size';
import type { grsaiModelConfig, TextSize, Resolution, InputPayloadType } from './types';
import { grsaiModelConfigs } from './config';
import { translateToEnglish } from '../../shared/translate';
import { needsTranslation } from '../tools';

/**
 * 获取模型配置
 */
export function getgrsaiModelConfig(modelId: string): grsaiModelConfig {
    const config = grsaiModelConfigs[modelId];
    if (!config) {
        throw new Error(`Model ${modelId} not found`);
    }
    return config;
}

/**
 * 构建输入参数
 */
export async function buildInputPayloadFromConfig(
    modelId: string,
    params: ImageGenerateParams
): Promise<InputPayloadType> {
    // 获取服务商配置
    const config = getgrsaiModelConfig(modelId);
    
    // 从 Provider Options 中获取 image_input
    const image_urls: string[] = (params.providerOptions as any)?.url?.image_input ?? [];

    // 分辨率：仅读取 providerOptions.image_resolution，非法值兜底 '4K'
    // 兼容两类值：
    // - '1K'...'8K'（多数生图模型）
    // - '1x'...'8x'（超分倍率，如 Topaz）
    const image_resolution: Resolution = (() => {
        const v = (params?.providerOptions as any)?.image_resolution;
        if (typeof v !== 'string') return '4K';
        const raw = v.trim();
        const up = raw.toUpperCase();
        if (up === '1K' || up === '2K' || up === '3K' || up === '4K' || up === '8K') return up as Resolution;
        const low = raw.toLowerCase();
        if (low === '1x' || low === '2x' || low === '4x' || low === '8x') return low as Resolution;
        return '4K';
    })();

    // 比例: 需要类型转换
    const pixelSize = params.size === 'match' ? undefined : (params.size as `${number}x${number}`);
    const image_size: TextSize = mapResolutionToSize(pixelSize, 'text');

    // 提示词翻译
    let prompt = params.prompt;
    if (config.enableTranslation && needsTranslation(prompt)) {
        try {
            prompt = await translateToEnglish(prompt);
        } catch (error) {
            // 翻译失败时使用原始提示词
            console.error(`[grsai][${modelId}] Failed to translate prompt:`, error);
        }
    }

    // 获取生成图片数量
    // 优先从 providerOptions._imagesCount 获取（当 useRequestBodyForCount 为 true 时）
    // 否则从 params.n 获取（当 useRequestBodyForCount 为 grsaise 时，AI SDK 会使用这个值）
    const imagesCount = (params.providerOptions as any)?._imagesCount ?? params.n;

    // 构建输入参数
    const result = await config.buildInput({
        prompt: prompt,
        image_urls: image_urls,
        size: params.size,
        aspectRatio: params.aspectRatio,
        seed: params.seed,
        image_size: image_size,
        image_resolution: image_resolution,
        providerOptions: params.providerOptions,
        n: imagesCount, // 传递生成图片数量（用于请求体）
    });

    return result;
}